import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, Stethoscope, TrendingUp, Edit2, Trash2, Plus, X, Bell, Video, StopCircle, Download, Filter, FileSpreadsheet, FileText, UserX, UserCheck, Eye, EyeOff, Wallet, CreditCard, RefreshCw, Star, ArrowLeft, BookOpen, Mail, Search, Phone, MapPin, Handshake, Building2, ExternalLink, Zap } from 'lucide-react';
import { getStatusLabel, getPaymentLabel, canAdminComplete } from './utils/appointmentStatus';
import { formatDate, formatDateTime } from './utils/formatDate';
import { buildArticlePatch, normalizeNewArticle } from './utils/articleContent';
import { loadAdminArticleDetail } from './utils/articleApi';
import ArticleFormModal from './admin/modals/ArticleFormModal';
import DoctorFormModal from './admin/modals/DoctorFormModal';
import ServiceFormModal from './admin/modals/ServiceFormModal';
import SubscriptionFormModal from './admin/modals/SubscriptionFormModal';
import AdminPartners from './AdminPartners';
import AdminPaymentsReconcile from './admin/AdminPaymentsReconcile';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

function AdminDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [revFilter, setRevFilter] = useState({ start: '', end: '', label: 'all' });
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [exchangeRate, setExchangeRate] = useState(null);
  const [newRate, setNewRate] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [showSubForm, setShowSubForm] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average: 0, distribution: {} });
  const [reviewFilter, setReviewFilter] = useState('all'); // all | submitted | hidden
  const [articles, setArticles] = useState([]);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleLoadingId, setArticleLoadingId] = useState(null);
  const [articleLoadError, setArticleLoadError] = useState('');
  const [savingArticle, setSavingArticle] = useState(false);
  const [refreshingArticles, setRefreshingArticles] = useState(false);
  const [contactMessages, setContactMessages] = useState([]);
  const [contactFilter, setContactFilter] = useState('all'); // all | new | handled
  const [editingSub, setEditingSub] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [showDoctorForm, setShowDoctorForm] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingPaymentAlerts, setPendingPaymentAlerts] = useState(() => {
    const saved = Number.parseInt(localStorage.getItem('admin_pending_payment_alerts') || '0', 10);
    return Number.isFinite(saved) && saved > 0 ? saved : 0;
  });
  const [paymentsRefreshKey, setPaymentsRefreshKey] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    payment_status: '',
    doctor_id: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const wsRef = useRef(null);
  const activeTabRef = useRef(activeTab);
  const fetchStatsRef = useRef(null);
  const fetchAppointmentsRef = useRef(null);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    let reconnectTimer;
    let shouldReconnect = true;

    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/api/ws/admin/notifications`);
        
        ws.onopen = () => {
          console.log('WebSocket connected');
        };
        
        ws.onmessage = (event) => {
          const notification = JSON.parse(event.data);
          console.log('Received notification:', notification);
          
          // Add notification to list
          setNotifications(prev => [notification, ...prev]);
          setUnreadCount(prev => prev + 1);
          
          // Show browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: notification.message,
              icon: '/favicon.ico'
            });
          }
          
          // Refresh stats if on stats tab
          if (activeTabRef.current === 'stats') {
            fetchStatsRef.current?.();
          }
          // Refresh appointments if on appointments tab
          if (activeTabRef.current === 'appointments') {
            fetchAppointmentsRef.current?.();
          }

          if (notification.type === 'payment_confirmed') {
            setPaymentsRefreshKey((key) => key + 1);
            if (activeTabRef.current !== 'payments-reconcile') {
              setPendingPaymentAlerts((count) => {
                const nextCount = count + 1;
                localStorage.setItem('admin_pending_payment_alerts', String(nextCount));
                return nextCount;
              });
            }
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
        
        ws.onclose = () => {
          if (shouldReconnect) {
            console.log('WebSocket disconnected, reconnecting...');
            reconnectTimer = window.setTimeout(connectWebSocket, 3000);
          }
        };
        
        wsRef.current = ws;
      } catch (error) {
        console.error('WebSocket connection error:', error);
      }
    };

    connectWebSocket();

    // Request notification permission
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      shouldReconnect = false;
      window.clearTimeout(reconnectTimer);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const openPaymentsTab = () => {
    setActiveTab('payments-reconcile');
    setPendingPaymentAlerts(0);
    localStorage.removeItem('admin_pending_payment_alerts');
  };

  const markNotificationsAsRead = () => {
    setUnreadCount(0);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    setShowNotifications(false);
  };

  // Helper to get admin auth headers
  const getAdminHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // End consultation (Admin action)
  const endConsultation = async (appointmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir terminer cette consultation ? Le lien vidéo sera fermé.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/consultation/${appointmentId}/end`, {
        method: 'POST',
        headers: getAdminHeaders()
      });
      
      if (response.ok) {
        alert('Consultation terminée avec succès');
        fetchAppointments();
      } else {
        const data = await response.json();
        if (response.status === 401 || response.status === 403) {
          alert('Session expirée. Veuillez vous reconnecter.');
          onLogout();
          return;
        }
        alert(data.detail || 'Erreur lors de la terminaison');
      }
    } catch (error) {
      console.error('Error ending consultation:', error);
      alert('Erreur de connexion');
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch revenue analytics
  const fetchExchangeRate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/exchange-rate`, { headers: getAdminHeaders() });
      const data = await res.json();
      setExchangeRate(data.rate);
    } catch (e) { console.error(e); }
  };

  const saveExchangeRate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/exchange-rate`, {
        method: 'PUT',
        headers: { ...getAdminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rate: parseFloat(newRate) })
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Erreur'); return; }
      setExchangeRate(data.rate);
      setNewRate('');
      alert(data.message);
    } catch (e) { alert('Erreur de connexion'); }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions`, { headers: getAdminHeaders() });
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
    } catch (e) { console.error(e); }
  };

  const fetchReviews = async () => {
    try {
      const params = reviewFilter !== 'all' ? `?status=${reviewFilter}` : '';
      const res = await fetch(`${API_URL}/api/admin/reviews${params}`, { headers: getAdminHeaders() });
      const data = await res.json();
      setReviews(data.reviews || []);
      setReviewStats(data.stats || { total: 0, average: 0, distribution: {} });
    } catch (e) { console.error(e); }
  };

  const toggleReviewVisibility = async (token, currentStatus) => {
    const action = currentStatus === 'hidden' ? 'unhide' : 'hide';
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/${token}/${action}`, {
        method: 'POST',
        headers: getAdminHeaders(),
      });
      if (res.ok) fetchReviews();
    } catch (e) { console.error(e); }
  };

  const saveSubscription = async (form) => {
    try {
      const isEdit = !!(editingSub && editingSub.subscription_id);
      const url = isEdit
        ? `${API_URL}/api/admin/subscriptions/${editingSub.subscription_id}`
        : `${API_URL}/api/admin/subscriptions`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { ...getAdminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) { const d = await res.json(); alert(d.detail || 'Erreur'); return; }
      setShowSubForm(false);
      setEditingSub(null);
      fetchSubscriptions();
    } catch (e) { alert('Erreur de connexion'); }
  };

  const deleteSubscription = async (id) => {
    if (!window.confirm('Supprimer cet abonnement ?')) return;
    try {
      await fetch(`${API_URL}/api/admin/subscriptions/${id}`, { method: 'DELETE', headers: getAdminHeaders() });
      fetchSubscriptions();
    } catch (e) { alert('Erreur de connexion'); }
  };

  const fetchRevenue = async () => {
    try {
      const params = new URLSearchParams();
      if (revFilter.start) params.set('start_date', revFilter.start);
      if (revFilter.end) params.set('end_date', revFilter.end);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_URL}/api/admin/revenue${qs}`, {
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      const data = await response.json();
      setRevenue(data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    }
  };

  // Fetch appointments
  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/appointments`, {
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      const data = await response.json();
      setAppointments(data.appointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  fetchStatsRef.current = fetchStats;
  fetchAppointmentsRef.current = fetchAppointments;

  // Fetch doctors (admin endpoint with suspension status)
  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/doctors`, {
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      const data = await response.json();
      setDoctors(data.doctors || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch services
  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/services`);
      const data = await response.json();
      setServices(data.services);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to appointments
  useEffect(() => {
    let result = [...appointments];
    
    if (filters.status) {
      result = result.filter(apt => apt.status === filters.status);
    }
    if (filters.payment_status) {
      result = result.filter(apt => apt.payment_status === filters.payment_status);
    }
    if (filters.doctor_id) {
      result = result.filter(apt => apt.doctor_id === filters.doctor_id);
    }
    if (filters.date_from) {
      result = result.filter(apt => apt.appointment_date >= filters.date_from);
    }
    if (filters.date_to) {
      result = result.filter(apt => apt.appointment_date <= filters.date_to);
    }
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(apt => 
        apt.patient_name?.toLowerCase().includes(search) ||
        apt.patient_phone?.includes(search)
      );
    }

    // Always display the most recent appointments first (chronological desc)
    result.sort((a, b) => {
      const dateA = new Date(`${a.appointment_date}T${a.appointment_time || '00:00'}`);
      const dateB = new Date(`${b.appointment_date}T${b.appointment_time || '00:00'}`);
      return dateB - dateA;
    });

    setFilteredAppointments(result);
  }, [appointments, filters]);

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: '',
      payment_status: '',
      doctor_id: '',
      date_from: '',
      date_to: '',
      search: ''
    });
  };

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/appointments/export?format=csv`, {
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      const data = await response.json();
      
      // Create and download CSV file
      const blob = new Blob([data.content], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || 'rendez_vous.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Erreur lors de l\'export');
    }
  };

  // Export to PDF (generates printable view)
  const exportToPDF = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/appointments/export?format=json`, {
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      const data = await response.json();
      
      // Create printable HTML
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Rendez-vous - Centre Médical La Trinité</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #4A90A4; text-align: center; }
            .header { text-align: center; margin-bottom: 30px; }
            .date { color: #666; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 12px; }
            th { background: #4A90A4; color: white; }
            tr:nth-child(even) { background: #f9f9f9; }
            .total { margin-top: 20px; font-weight: bold; }
            .footer { margin-top: 40px; text-align: center; color: #666; font-size: 11px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Centre Médical La Trinité</h1>
            <p class="date">Liste des Rendez-vous - Générée le ${formatDate(new Date())}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Heure</th>
                <th>Patient</th>
                <th>Téléphone</th>
                <th>Service</th>
                <th>Médecin</th>
                <th>Statut</th>
                <th>Montant (CDF)</th>
                <th>Paiement</th>
              </tr>
            </thead>
            <tbody>
              ${data.data.map(apt => `
                <tr>
                  <td>${apt.date}</td>
                  <td>${apt.heure}</td>
                  <td>${apt.patient}</td>
                  <td>${apt.telephone}</td>
                  <td>${apt.service}</td>
                  <td>${apt.medecin}</td>
                  <td>${apt.statut}</td>
                  <td>${apt.montant_cdf?.toLocaleString() || '0'}</td>
                  <td>${apt.paiement}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <p class="total">Total: ${data.total_records} rendez-vous</p>
          <p class="total">Montant Total: ${data.data.reduce((sum, apt) => sum + (apt.montant_cdf || 0), 0).toLocaleString()} CDF</p>
          <div class="footer">
            <p>Centre Médical La Trinité - MATADI, RDC</p>
          </div>
        </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error('Export error:', error);
      alert('Erreur lors de l\'export PDF');
    }
  };

  // Save doctor
  const saveDoctor = async (doctorData) => {
    try {
      const method = editingDoctor ? 'PUT' : 'POST';
      const url = editingDoctor 
        ? `${API_URL}/api/admin/doctors/${editingDoctor.doctor_id}`
        : `${API_URL}/api/admin/doctors`;
      
      // Remove photo_file from data sent to API
      const { photo_file, otherCity, ...dataToSend } = doctorData;
      
      const response = await fetch(url, {
        method,
        headers: getAdminHeaders(),
        body: JSON.stringify(dataToSend)
      });
      
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      
      const result = await response.json();
      
      if (!response.ok) {
        // Handle error from backend
        const errorMsg = result.detail || 'Erreur lors de l\'enregistrement';
        alert(errorMsg);
        return;
      }
      
      // If new doctor and has photo file, upload it
      if (!editingDoctor && photo_file && result.doctor_id) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', photo_file);
        
        await fetch(`${API_URL}/api/admin/doctors/${result.doctor_id}/photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
          body: formDataUpload
        });
      }
      
      setShowDoctorForm(false);
      setEditingDoctor(null);
      fetchDoctors();
      alert('Médecin enregistré avec succès');
    } catch (error) {
      console.error('Error saving doctor:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  // Delete doctor
  const deleteDoctor = async (doctorId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce médecin?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/doctors/${doctorId}`, {
        method: 'DELETE',
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      fetchDoctors();
      alert('Médecin supprimé avec succès');
    } catch (error) {
      console.error('Error deleting doctor:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Suspend doctor
  const suspendDoctor = async (doctorId, doctorName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir suspendre ${doctorName}? Le médecin ne pourra plus se connecter.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/doctors/${doctorId}/suspend`, {
        method: 'POST',
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      if (response.ok) {
        fetchDoctors();
        alert('Médecin suspendu avec succès');
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la suspension');
      }
    } catch (error) {
      console.error('Error suspending doctor:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  // Reactivate doctor
  const reactivateDoctor = async (doctorId, doctorName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir réactiver ${doctorName}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/doctors/${doctorId}/reactivate`, {
        method: 'POST',
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      if (response.ok) {
        fetchDoctors();
        alert('Médecin réactivé avec succès');
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la réactivation');
      }
    } catch (error) {
      console.error('Error reactivating doctor:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  // Save service
  const saveService = async (serviceData) => {
    try {
      const method = editingService ? 'PUT' : 'POST';
      const url = editingService 
        ? `${API_URL}/api/admin/services/${editingService.service_id}`
        : `${API_URL}/api/admin/services`;
      
      const response = await fetch(url, {
        method,
        headers: getAdminHeaders(),
        body: JSON.stringify(serviceData)
      });
      
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      
      setShowServiceForm(false);
      setEditingService(null);
      fetchServices();
      alert('Service enregistré avec succès');
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  // Handle doctor photo upload - updates editingDoctor directly
  const handleDoctorPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // If editing existing doctor, upload immediately
    if (editingDoctor && editingDoctor.doctor_id) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      try {
        const response = await fetch(`${API_URL}/api/admin/doctors/${editingDoctor.doctor_id}/photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
          body: formDataUpload
        });
        
        if (response.ok) {
          const data = await response.json();
          setEditingDoctor(prev => ({ ...prev, photo_url: data.photo_url }));
          alert('Photo mise à jour !');
          fetchDoctors();
        } else {
          const err = await response.json();
          alert(err.detail || 'Erreur lors de l\'upload');
        }
      } catch (error) {
        console.error('Upload error:', error);
        alert('Erreur de connexion');
      }
    } else {
      // For new doctor, store file for later upload and show preview
      setEditingDoctor(prev => ({ ...prev, photo_file: file, photo_url: URL.createObjectURL(file) }));
    }
  };

  // Delete service
  const deleteService = async (serviceId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce service?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/services/${serviceId}`, {
        method: 'DELETE',
        headers: getAdminHeaders()
      });
      if (response.status === 401 || response.status === 403) {
        onLogout();
        return;
      }
      fetchServices();
      alert('Service supprimé avec succès');
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // ==================== Journal Médical (CMS) ====================
  const fetchArticles = async () => {
    setRefreshingArticles(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/journal`, { headers: getAdminHeaders() });
      if (res.status === 401 || res.status === 403) { onLogout(); return; }
      const data = await res.json();
      setArticles(data.articles || []);
    } catch (e) {
      console.error('Error fetching articles:', e);
    } finally {
      setRefreshingArticles(false);
    }
  };

  const openArticleForEdit = async (articleSummary) => {
    setArticleLoadError('');
    setArticleLoadingId(articleSummary.article_id);
    try {
      const completeArticle = await loadAdminArticleDetail({
        apiUrl: API_URL,
        articleId: articleSummary.article_id,
        headers: getAdminHeaders(),
      });
      setEditingArticle({ ...completeArticle, __contentLoaded: true });
      setShowArticleForm(true);
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        onLogout();
        return;
      }
      console.error('Article detail loading error:', error);
      setEditingArticle(null);
      setShowArticleForm(false);
      setArticleLoadError(error.message || "Impossible de charger le contenu complet de l'article.");
    } finally {
      setArticleLoadingId(null);
    }
  };

  const closeArticleForm = () => {
    if (savingArticle) return;
    setShowArticleForm(false);
    setEditingArticle(null);
  };

  const saveArticle = async (payload) => {
    const isEdit = !!editingArticle;
    const requestPayload = isEdit
      ? buildArticlePatch(editingArticle, payload)
      : normalizeNewArticle(payload);

    if (isEdit && Object.keys(requestPayload).length === 0) {
      alert('Aucune modification à enregistrer.');
      return;
    }
    const url = isEdit
      ? `${API_URL}/api/admin/journal/${editingArticle.article_id}`
      : `${API_URL}/api/admin/journal`;
    const method = isEdit ? 'PATCH' : 'POST';
    setSavingArticle(true);
    try {
      const res = await fetch(url, {
        method,
        headers: { ...getAdminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload),
      });
      if (res.status === 401 || res.status === 403) { onLogout(); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Erreur lors de l\'enregistrement');
        return;
      }
      setShowArticleForm(false);
      setEditingArticle(null);
      fetchArticles();
    } catch (e) {
      console.error('Save article error:', e);
      alert('Erreur de connexion');
    } finally {
      setSavingArticle(false);
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/journal/${articleId}`, {
        method: 'DELETE', headers: getAdminHeaders(),
      });
      if (res.status === 401 || res.status === 403) { onLogout(); return; }
      fetchArticles();
    } catch (e) {
      console.error('Delete article error:', e);
    }
  };

  // ==================== Messages de contact ====================
  const fetchContactMessages = async () => {
    try {
      const url = contactFilter === 'all'
        ? `${API_URL}/api/admin/contact-messages`
        : `${API_URL}/api/admin/contact-messages?status=${contactFilter}`;
      const res = await fetch(url, { headers: getAdminHeaders() });
      if (res.status === 401 || res.status === 403) { onLogout(); return; }
      const data = await res.json();
      setContactMessages(data.messages || []);
    } catch (e) {
      console.error('Error fetching contact messages:', e);
    }
  };

  const toggleContactStatus = async (messageId, currentStatus) => {
    const newStatus = currentStatus === 'handled' ? 'new' : 'handled';
    try {
      await fetch(`${API_URL}/api/admin/contact-messages/${messageId}?status=${newStatus}`, {
        method: 'PATCH', headers: getAdminHeaders(),
      });
      fetchContactMessages();
    } catch (e) {
      console.error('Toggle contact status error:', e);
    }
  };

  useEffect(() => {
    fetchStats();
    if (activeTab === 'appointments') fetchAppointments();
    if (activeTab === 'doctors') fetchDoctors();
    if (activeTab === 'services') { fetchServices(); fetchExchangeRate(); }
    if (activeTab === 'revenue') fetchRevenue();
    if (activeTab === 'subscriptions') fetchSubscriptions();
    if (activeTab === 'reviews') fetchReviews();
    if (activeTab === 'journal') fetchArticles();
    if (activeTab === 'messages') fetchContactMessages();
  }, [activeTab, revFilter, reviewFilter, contactFilter]);

  return (
    <div className="admin-dashboard">
      {/* Admin Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <a
              href="/"
              data-testid="admin-back-to-site"
              title="Retour au site officiel"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#fff', textDecoration: 'none', fontSize: '0.85rem', opacity: 0.85 }}
            >
              <ArrowLeft size={16} /> Site officiel
            </a>
            <h1 style={{ margin: 0 }}>Dashboard Administrateur</h1>
          </div>
          <div className="admin-header-actions">
            <div className="notification-container">
              <button 
                className="notification-bell"
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) markNotificationsAsRead();
                }}
                data-testid="notification-bell"
              >
                <Bell size={24} />
                {unreadCount > 0 && (
                  <span className="notification-badge" data-testid="notification-count">
                    {unreadCount}
                  </span>
                )}
              </button>
              
              {/* Notifications Panel */}
              {showNotifications && (
                <div className="notifications-panel" data-testid="notifications-panel">
                  <div className="notifications-header">
                    <h3>Notifications</h3>
                    <button onClick={clearNotifications} className="clear-btn">
                      Tout effacer
                    </button>
                  </div>
                  <div className="notifications-list">
                    {notifications.length === 0 ? (
                      <p className="no-notifications">Aucune notification</p>
                    ) : (
                      notifications.map((notif, index) => (
                        <div 
                          key={index} 
                          className={`notification-item notification-${notif.type}`}
                          data-testid={`notification-${index}`}
                        >
                          <div className="notification-title">{notif.title}</div>
                          <div className="notification-message">{notif.message}</div>
                          <div className="notification-time">
                            {formatDateTime(notif.timestamp)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button onClick={onLogout} className="btn-secondary" data-testid="admin-logout">
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Admin Navigation */}
      <div className="admin-nav">
        <button 
          className={activeTab === 'stats' ? 'active' : ''}
          onClick={() => setActiveTab('stats')}
          data-testid="admin-nav-stats"
        >
          <TrendingUp size={20} /> Statistiques
        </button>
        <button 
          className={activeTab === 'appointments' ? 'active' : ''}
          onClick={() => setActiveTab('appointments')}
          data-testid="admin-nav-appointments"
        >
          <Calendar size={20} /> Rendez-vous
        </button>
        <button 
          className={activeTab === 'revenue' ? 'active' : ''}
          onClick={() => setActiveTab('revenue')}
          data-testid="admin-nav-revenue"
        >
          <Wallet size={20} /> Revenus
        </button>
        <button 
          className={activeTab === 'subscriptions' ? 'active' : ''}
          onClick={() => setActiveTab('subscriptions')}
          data-testid="admin-nav-subscriptions"
        >
          <CreditCard size={20} /> Abonnements
        </button>
        <button 
          className={activeTab === 'doctors' ? 'active' : ''}
          onClick={() => setActiveTab('doctors')}
          data-testid="admin-nav-doctors"
        >
          <Users size={20} /> Médecins
        </button>
        <button 
          className={activeTab === 'services' ? 'active' : ''}
          onClick={() => setActiveTab('services')}
          data-testid="admin-nav-services"
        >
          <Stethoscope size={20} /> Services
        </button>
        <button 
          className={activeTab === 'reviews' ? 'active' : ''}
          onClick={() => setActiveTab('reviews')}
          data-testid="admin-nav-reviews"
        >
          <Star size={20} /> Avis Patients
        </button>
        <button 
          className={activeTab === 'journal' ? 'active' : ''}
          onClick={() => setActiveTab('journal')}
          data-testid="admin-nav-journal"
        >
          <BookOpen size={20} /> Journal
        </button>
        <button 
          className={activeTab === 'messages' ? 'active' : ''}
          onClick={() => setActiveTab('messages')}
          data-testid="admin-nav-messages"
        >
          <Mail size={20} /> Messages
        </button>
        <button 
          className={activeTab === 'partners' ? 'active' : ''}
          onClick={() => setActiveTab('partners')}
          data-testid="admin-nav-partners"
        >
          <Handshake size={20} /> Partenaires
        </button>
        <button
          className={activeTab === 'payments-reconcile' ? 'active' : ''}
          onClick={openPaymentsTab}
          data-testid="admin-nav-payments-reconcile"
        >
          <RefreshCw size={20} /> Paiements SerdiPay
          {pendingPaymentAlerts > 0 && (
            <span className="payment-alert-badge" data-testid="admin-payments-pending-badge">
              {pendingPaymentAlerts} en attente
            </span>
          )}
        </button>
      </div>

      <div className="admin-content">
        {/* Statistics Tab */}
        {activeTab === 'stats' && stats && (
          <div className="stats-grid" data-testid="admin-stats">
            <div className="stat-card">
              <h3>Total Rendez-vous</h3>
              <p className="stat-number">{stats.total_appointments}</p>
            </div>
            <div className="stat-card">
              <h3>Confirmés</h3>
              <p className="stat-number">{stats.confirmed_appointments}</p>
            </div>
            <div className="stat-card">
              <h3>En Attente</h3>
              <p className="stat-number">{stats.pending_appointments}</p>
            </div>
            <div className="stat-card">
              <h3>Revenu Total</h3>
              <p className="stat-number">{Math.round(stats.total_revenue_cdf || 0).toLocaleString('fr-FR')} CDF</p>
            </div>
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div data-testid="admin-revenue">
            <div className="admin-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <h2><Wallet size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />Revenus Mobile Money</h2>
              <button
                data-testid="revenue-export-excel"
                onClick={async () => {
                  try {
                    const params = new URLSearchParams();
                    if (revFilter.start) params.set('start_date', revFilter.start);
                    if (revFilter.end) params.set('end_date', revFilter.end);
                    const qs = params.toString() ? `?${params.toString()}` : '';
                    const res = await fetch(`${API_URL}/api/admin/revenue/export${qs}`, { headers: getAdminHeaders() });
                    if (!res.ok) throw new Error('Export refusé');
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const cd = res.headers.get('Content-Disposition') || '';
                    const m = cd.match(/filename="(.+)"/);
                    a.download = m ? m[1] : 'revenus.xlsx';
                    a.click();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    alert("Erreur lors de l'export Excel");
                  }
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
              >
                <FileSpreadsheet size={18} /> Exporter Excel
              </button>
            </div>

            {/* Period filter */}
            <div data-testid="revenue-period-filter" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, margin: '0 0 1.25rem' }}>
              {[
                { key: 'all', label: 'Tout' },
                { key: 'month', label: 'Ce mois' },
                { key: 'lastmonth', label: 'Mois dernier' }
              ].map((p) => (
                <button
                  key={p.key}
                  data-testid={`revenue-filter-${p.key}`}
                  onClick={() => {
                    const now = new Date();
                    if (p.key === 'all') setRevFilter({ start: '', end: '', label: 'all' });
                    if (p.key === 'month') {
                      const start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                      setRevFilter({ start, end: now.toISOString().slice(0, 10), label: 'month' });
                    }
                    if (p.key === 'lastmonth') {
                      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                      const last = new Date(now.getFullYear(), now.getMonth(), 0);
                      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                      setRevFilter({ start: fmt(first), end: fmt(last), label: 'lastmonth' });
                    }
                  }}
                  style={{
                    padding: '6px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 14,
                    border: revFilter.label === p.key ? '2px solid #0d9488' : '1px solid #cbd5e1',
                    background: revFilter.label === p.key ? '#0d9488' : '#fff',
                    color: revFilter.label === p.key ? '#fff' : '#334155',
                    fontWeight: revFilter.label === p.key ? 700 : 400
                  }}
                >
                  {p.label}
                </button>
              ))}
              <span style={{ color: '#94a3b8', margin: '0 4px' }}>|</span>
              <label style={{ fontSize: 13, color: '#64748b' }}>Du</label>
              <input type="date" data-testid="revenue-filter-start" value={customRange.start}
                onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })}
                style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }} />
              <label style={{ fontSize: 13, color: '#64748b' }}>Au</label>
              <input type="date" data-testid="revenue-filter-end" value={customRange.end}
                onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })}
                style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }} />
              <button
                data-testid="revenue-filter-apply"
                onClick={() => setRevFilter({ start: customRange.start, end: customRange.end, label: 'custom' })}
                disabled={!customRange.start && !customRange.end}
                style={{
                  padding: '6px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 14,
                  border: 'none', background: revFilter.label === 'custom' ? '#0d9488' : '#334155', color: '#fff', fontWeight: 600,
                  opacity: (!customRange.start && !customRange.end) ? 0.5 : 1
                }}
              >
                Appliquer
              </button>
              {revFilter.label !== 'all' && (
                <span style={{ fontSize: 13, color: '#0d9488', fontWeight: 600 }} data-testid="revenue-filter-active">
                  Période : {revFilter.start || '…'} → {revFilter.end || "aujourd'hui"}
                </span>
              )}
            </div>

            {!revenue ? (
              <p style={{ padding: '2rem', color: '#64748b' }}>Chargement des revenus...</p>
            ) : (
              <>
                <div className="stats-grid" data-testid="revenue-summary">
                  <div className="stat-card">
                    <h3>Revenu Total (CDF)</h3>
                    <p className="stat-number">{(revenue.total_revenue_cdf || 0).toLocaleString('fr-FR')}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Transactions Payées</h3>
                    <p className="stat-number">{revenue.total_transactions}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Paiements USD</h3>
                    <p className="stat-number">{revenue.by_currency?.USD?.count || 0}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Paiements CDF</h3>
                    <p className="stat-number">{revenue.by_currency?.CDF?.count || 0}</p>
                  </div>
                </div>
                <p data-testid="revenue-rate-info" style={{ fontSize: 13, color: '#64748b', margin: '0.5rem 0 0' }}>
                  Taux de conversion appliqué : <strong>1 USD = {Math.round(revenue.cdf_rate || 2200).toLocaleString('fr-FR')} CDF</strong> (taux fixe configuré par la clinique, modifiable sur demande)
                </p>

                {/* By currency */}
                <div className="revenue-block" data-testid="revenue-by-currency">
                  <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Par devise</h3>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {(revenue.by_currency_list || []).map((c) => (
                      <div key={c.currency} className="stat-card" style={{ flex: '1 1 200px', minWidth: 200 }}>
                        <h3>{c.currency} · {c.count} txn</h3>
                        <p className="stat-number" style={{ fontSize: '1.6rem' }}>{(c.amount || 0).toLocaleString('fr-FR')} {c.currency}</p>
                        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>≈ {Math.round(c.amount_cdf).toLocaleString('fr-FR')} CDF</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* By doctor */}
                <div className="revenue-block" data-testid="revenue-by-doctor">
                  <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Par médecin (consultations)</h3>
                  {(revenue.by_doctor || []).map((doc) => {
                    const max = Math.max(...revenue.by_doctor.map(o => o.amount_cdf), 1);
                    return (
                      <div key={doc.doctor} className="op-row" style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                          <span style={{ fontWeight: 600 }}>{doc.doctor}</span>
                          <span>{Math.round(doc.amount_cdf).toLocaleString('fr-FR')} CDF · {doc.count} txn</span>
                        </div>
                        <div style={{ background: '#e2e8f0', borderRadius: 6, height: 10 }}>
                          <div style={{ width: `${(doc.amount_cdf / max) * 100}%`, background: '#6366f1', height: 10, borderRadius: 6, transition: 'width .4s' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* By operator */}
                <div className="revenue-block" data-testid="revenue-by-operator">
                  <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Par opérateur</h3>
                  {(revenue.by_operator || []).map((op) => {
                    const max = Math.max(...revenue.by_operator.map(o => o.amount_cdf), 1);
                    return (
                      <div key={op.operator} className="op-row" style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                          <span style={{ fontWeight: 600 }}>{op.operator}</span>
                          <span>{Math.round(op.amount_cdf).toLocaleString('fr-FR')} CDF · {op.count} txn</span>
                        </div>
                        <div style={{ background: '#e2e8f0', borderRadius: 6, height: 10 }}>
                          <div style={{ width: `${(op.amount_cdf / max) * 100}%`, background: '#0d9488', height: 10, borderRadius: 6, transition: 'width .4s' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Daily chart */}
                <div className="revenue-block" data-testid="revenue-daily">
                  <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Encaissements par jour</h3>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, overflowX: 'auto', padding: '0 4px' }}>
                    {(revenue.daily || []).map((d) => {
                      const max = Math.max(...revenue.daily.map(x => x.amount_cdf), 1);
                      return (
                        <div key={d.date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 34 }} title={`${Math.round(d.amount_cdf).toLocaleString('fr-FR')} CDF`}>
                          <div style={{ width: 22, height: `${Math.max((d.amount_cdf / max) * 110, 4)}px`, background: '#0d9488', borderRadius: '4px 4px 0 0' }} />
                          <span style={{ fontSize: 9, color: '#64748b', marginTop: 4, transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{d.date.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Recent transactions */}
                <div className="revenue-block" data-testid="revenue-recent">
                  <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Transactions récentes</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="admin-table" style={{ width: '100%', fontSize: 14 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: 8 }}>Date</th>
                          <th style={{ textAlign: 'left', padding: 8 }}>Patient</th>
                          <th style={{ textAlign: 'left', padding: 8 }}>Médecin</th>
                          <th style={{ textAlign: 'left', padding: 8 }}>Opérateur</th>
                          <th style={{ textAlign: 'right', padding: 8 }}>Montant</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(revenue.recent || []).map((t) => (
                          <tr key={t.transaction_id} style={{ borderTop: '1px solid #e2e8f0' }}>
                            <td style={{ padding: 8 }}>{formatDate(t.created_at)}</td>
                            <td style={{ padding: 8 }}>{t.patient_name}</td>
                            <td style={{ padding: 8 }}>{t.doctor}</td>
                            <td style={{ padding: 8 }}>{t.operator_name}</td>
                            <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{t.amount} {t.currency}</td>
                          </tr>
                        ))}
                        {(revenue.recent || []).length === 0 && (
                          <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#64748b' }}>Aucune transaction pour le moment</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div data-testid="admin-appointments">
            <div className="admin-section-header">
              <h2>Gestion des Rendez-vous</h2>
              <div className="header-actions">
                <button 
                  className={`btn-filter ${showFilters ? 'active' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={18} /> Filtres
                </button>
                <button className="btn-export" onClick={exportToCSV}>
                  <FileSpreadsheet size={18} /> Excel (CSV)
                </button>
                <button className="btn-export" onClick={exportToPDF}>
                  <FileText size={18} /> PDF
                </button>
              </div>
            </div>
            
            {/* Filters Panel */}
            {showFilters && (
              <div className="filters-panel">
                <div className="filters-row">
                  <div className="filter-group">
                    <label>Recherche</label>
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => setFilters({...filters, search: e.target.value})}
                      placeholder="Nom ou téléphone..."
                    />
                  </div>
                  <div className="filter-group">
                    <label>Statut</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                      <option value="">Tous</option>
                      <option value="pending">En attente</option>
                      <option value="pending_payment">Attente paiement</option>
                      <option value="confirmed">Confirmé</option>
                      <option value="in-progress">En cours</option>
                      <option value="completed">Terminé</option>
                      <option value="cancelled">Annulé</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Paiement</label>
                    <select
                      value={filters.payment_status}
                      onChange={(e) => setFilters({...filters, payment_status: e.target.value})}
                    >
                      <option value="">Tous</option>
                      <option value="paid">Payé</option>
                      <option value="pending">En attente</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Médecin</label>
                    <select
                      value={filters.doctor_id}
                      onChange={(e) => setFilters({...filters, doctor_id: e.target.value})}
                    >
                      <option value="">Tous</option>
                      {doctors.map(doc => (
                        <option key={doc.doctor_id} value={doc.doctor_id}>{doc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Date début</label>
                    <input
                      type="date"
                      value={filters.date_from}
                      onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Date fin</label>
                    <input
                      type="date"
                      value={filters.date_to}
                      onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                    />
                  </div>
                </div>
                <button className="btn-reset" onClick={resetFilters}>
                  Réinitialiser les filtres
                </button>
              </div>
            )}
            
            <div className="table-info">
              <span>{filteredAppointments.length} rendez-vous affichés sur {appointments.length}</span>
              <span className="total-amount">
                Total: {filteredAppointments.reduce((sum, apt) => sum + (apt.service?.price_cdf || 0), 0).toLocaleString()} CDF
              </span>
            </div>
            
            {loading ? (
              <p>Chargement...</p>
            ) : (
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Heure</th>
                      <th>Patient</th>
                      <th>Téléphone</th>
                      <th>Service</th>
                      <th>Médecin</th>
                      <th>Montant (CDF)</th>
                      <th>Statut</th>
                      <th>Paiement</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.map((apt) => (
                      <tr key={apt.appointment_id}>
                        <td>{formatDate(apt.appointment_date)}</td>
                        <td>{apt.appointment_time}</td>
                        <td>{apt.patient_name || (apt.patient_info ? `${apt.patient_info.first_name} ${apt.patient_info.last_name}` : 'N/A')}</td>
                        <td>{apt.patient_phone || '-'}</td>
                        <td>
                          {apt.service?.name}
                          {(apt.service?.service_id === 'teleconsultation' || (apt.service?.name || '').toLowerCase().includes('téléconsult')) && (
                            <span className="badge badge-video" style={{marginLeft: '0.5rem'}}>
                              <Video size={12} /> Vidéo
                            </span>
                          )}
                        </td>
                        <td>{apt.doctor?.name}</td>
                        <td className="amount-cell">{apt.service?.price_cdf?.toLocaleString() || 0}</td>
                        <td>
                          <span className={`badge badge-${apt.status}`}>
                            {getStatusLabel(apt.status)}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge-${apt.payment_status}`}>
                            {getPaymentLabel(apt.payment_status)}
                          </span>
                        </td>
                        <td>
                          {/* Admin peut terminer toute consultation confirmée ou en cours */}
                          {canAdminComplete(apt.status) && (
                            <button
                              onClick={() => endConsultation(apt.appointment_id)}
                              className="btn-icon-danger"
                              title="Terminer la consultation"
                            >
                              <StopCircle size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Doctors Tab */}
        {activeTab === 'doctors' && (
          <div data-testid="admin-doctors">
            <div className="admin-section-header">
              <h2>Gestion des Médecins</h2>
              <button 
                className="btn-primary"
                onClick={() => { setEditingDoctor(null); setShowDoctorForm(true); }}
                data-testid="add-doctor-btn"
              >
                <Plus size={20} /> Ajouter un Médecin
              </button>
            </div>

            {/* Search bar */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#F8FAFC', border: '1px solid #E2E8F0',
              padding: '10px 14px', borderRadius: 10, marginBottom: 16, maxWidth: 520
            }}>
              <Search size={18} color="#64748B" />
              <input
                type="text"
                placeholder="Rechercher un médecin (nom, spécialité, ville, email, téléphone)"
                value={doctorSearch}
                onChange={(e) => setDoctorSearch(e.target.value)}
                data-testid="doctors-search-input"
                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.9rem' }}
              />
              {doctorSearch && (
                <button
                  onClick={() => setDoctorSearch('')}
                  data-testid="doctors-search-clear"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: 2 }}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {loading ? (
              <p>Chargement...</p>
            ) : (() => {
              const q = doctorSearch.trim().toLowerCase();
              const filtered = q ? doctors.filter(d =>
                (d.name || '').toLowerCase().includes(q) ||
                (d.specialty || '').toLowerCase().includes(q) ||
                (d.city || '').toLowerCase().includes(q) ||
                (d.email || '').toLowerCase().includes(q) ||
                (d.phone || '').toLowerCase().includes(q)
              ) : doctors;

              if (filtered.length === 0) {
                return (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#64748B', background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 10 }}>
                    {q ? `Aucun médecin ne correspond à « ${doctorSearch} »` : 'Aucun médecin enregistré.'}
                  </div>
                );
              }

              return (
                <div style={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 12, background: 'white' }}>
                  <table data-testid="doctors-table" style={{
                    width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', minWidth: 900
                  }}>
                    <thead>
                      <tr style={{ background: '#F1F5F9', textAlign: 'left', borderBottom: '2px solid #CBD5E1' }}>
                        <th style={{ padding: '12px 10px', fontWeight: 600, color: '#334155' }}>Photo</th>
                        <th style={{ padding: '12px 10px', fontWeight: 600, color: '#334155' }}>Nom</th>
                        <th style={{ padding: '12px 10px', fontWeight: 600, color: '#334155' }}>Spécialité</th>
                        <th style={{ padding: '12px 10px', fontWeight: 600, color: '#334155' }}>Ville</th>
                        <th style={{ padding: '12px 10px', fontWeight: 600, color: '#334155' }}>Contact</th>
                        <th style={{ padding: '12px 10px', fontWeight: 600, color: '#334155', textAlign: 'center' }}>Jours dispo.</th>
                        <th style={{ padding: '12px 10px', fontWeight: 600, color: '#334155', textAlign: 'center' }}>Statut</th>
                        <th style={{ padding: '12px 10px', fontWeight: 600, color: '#334155', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((doctor) => {
                        const imgSrc = (!doctor.photo_url || /via\.placeholder|placehold\.co/i.test(doctor.photo_url))
                          ? '/doctor-placeholder.svg' : doctor.photo_url;
                        const daysCount = Array.isArray(doctor.available_days) ? doctor.available_days.length : 0;
                        return (
                          <tr key={doctor.doctor_id}
                              data-testid={`doctor-row-${doctor.doctor_id}`}
                              style={{ borderBottom: '1px solid #E2E8F0', background: doctor.suspended ? '#FEF2F2' : 'white' }}>
                            <td style={{ padding: '10px' }}>
                              <img
                                src={imgSrc}
                                alt={doctor.name}
                                onError={(e) => { e.target.onerror = null; e.target.src = '/doctor-placeholder.svg'; }}
                                style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid #E2E8F0' }}
                              />
                            </td>
                            <td style={{ padding: '10px', fontWeight: 600, color: '#0F172A' }}>
                              {doctor.name}
                              {doctor.bio && (
                                <div style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 400, marginTop: 2 }}>
                                  {doctor.bio.length > 60 ? doctor.bio.slice(0, 60) + '…' : doctor.bio}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px', color: '#475569' }}>{doctor.specialty || '—'}</td>
                            <td style={{ padding: '10px', color: '#475569' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <MapPin size={13} color="#94A3B8" /> {doctor.city || '—'}
                              </span>
                            </td>
                            <td style={{ padding: '10px', color: '#475569', fontSize: '0.82rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Mail size={12} color="#94A3B8" />
                                <span>{doctor.email || <em style={{ color: '#CBD5E1' }}>—</em>}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                                <Phone size={12} color="#94A3B8" />
                                <span>{doctor.phone || <em style={{ color: '#CBD5E1' }}>—</em>}</span>
                              </div>
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center', color: '#475569' }}>
                              <span style={{
                                display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                                background: daysCount >= 5 ? '#DCFCE7' : daysCount > 0 ? '#FEF3C7' : '#FEE2E2',
                                color: daysCount >= 5 ? '#166534' : daysCount > 0 ? '#92400E' : '#991B1B',
                                fontSize: '0.78rem', fontWeight: 600,
                              }}>
                                {daysCount} j/sem
                              </span>
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              {doctor.suspended ? (
                                <span style={{
                                  display: 'inline-block', padding: '3px 10px', borderRadius: 999,
                                  background: '#FEE2E2', color: '#991B1B', fontSize: '0.75rem', fontWeight: 700
                                }}>Suspendu</span>
                              ) : (
                                <span style={{
                                  display: 'inline-block', padding: '3px 10px', borderRadius: 999,
                                  background: '#DCFCE7', color: '#166534', fontSize: '0.75rem', fontWeight: 700
                                }}>Actif</span>
                              )}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>
                              <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                <button
                                  onClick={() => { setEditingDoctor(doctor); setShowDoctorForm(true); }}
                                  data-testid={`edit-doctor-${doctor.doctor_id}`}
                                  title="Modifier"
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    padding: '6px 10px', border: '1px solid #CBD5E1', background: 'white',
                                    borderRadius: 6, cursor: 'pointer', color: '#334155', fontSize: '0.78rem'
                                  }}
                                >
                                  <Edit2 size={13} /> Modifier
                                </button>
                                {doctor.suspended ? (
                                  <button
                                    onClick={() => reactivateDoctor(doctor.doctor_id, doctor.name)}
                                    data-testid={`reactivate-doctor-${doctor.doctor_id}`}
                                    title="Réactiver"
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '6px 10px', border: 'none', background: '#16A34A',
                                      borderRadius: 6, cursor: 'pointer', color: 'white', fontSize: '0.78rem', fontWeight: 600
                                    }}
                                  >
                                    <UserCheck size={13} /> Réactiver
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => suspendDoctor(doctor.doctor_id, doctor.name)}
                                    data-testid={`suspend-doctor-${doctor.doctor_id}`}
                                    title="Suspendre"
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '6px 10px', border: '1px solid #F59E0B', background: 'white',
                                      borderRadius: 6, cursor: 'pointer', color: '#B45309', fontSize: '0.78rem'
                                    }}
                                  >
                                    <UserX size={13} /> Suspendre
                                  </button>
                                )}
                                <button
                                  onClick={() => deleteDoctor(doctor.doctor_id)}
                                  data-testid={`delete-doctor-${doctor.doctor_id}`}
                                  title="Supprimer"
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    padding: '6px 10px', border: 'none', background: '#DC2626',
                                    borderRadius: 6, cursor: 'pointer', color: 'white', fontSize: '0.78rem', fontWeight: 600
                                  }}
                                >
                                  <Trash2 size={13} /> Supprimer
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{
                    padding: '10px 14px', borderTop: '1px solid #E2E8F0',
                    background: '#F8FAFC', fontSize: '0.82rem', color: '#64748B'
                  }} data-testid="doctors-table-footer">
                    {filtered.length} médecin{filtered.length > 1 ? 's' : ''} affiché{filtered.length > 1 ? 's' : ''}
                    {q && ` sur ${doctors.length}`}
                  </div>
                </div>
              );
            })()}

            {/* Doctor Form Modal */}
            {showDoctorForm && (
              <DoctorFormModal
                doctor={editingDoctor}
                onSave={saveDoctor}
                onClose={() => { setShowDoctorForm(false); setEditingDoctor(null); }}
              />
            )}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div data-testid="admin-services">
            <div className="admin-section-header">
              <h2>Gestion des Services & Tarification</h2>
              <button 
                className="btn-primary"
                onClick={() => { setEditingService(null); setShowServiceForm(true); }}
                data-testid="add-service-btn"
              >
                <Plus size={20} /> Ajouter un Service
              </button>
            </div>

            {/* Exchange rate management */}
            <div data-testid="exchange-rate-card" style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 12, padding: '1rem 1.25rem', margin: '0 0 1.25rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
              <RefreshCw size={20} color="#0d9488" />
              <div style={{ flex: '1 1 260px' }}>
                <strong style={{ color: '#134e4a' }}>Taux de change USD → CDF</strong>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>
                  Taux actuel : <strong data-testid="current-exchange-rate">{exchangeRate ? `1 USD = ${Math.round(exchangeRate).toLocaleString('fr-FR')} CDF` : 'chargement…'}</strong> — appliqué aux conversions de prix et aux rapports de revenus
                </p>
              </div>
              <input
                type="number"
                placeholder="Nouveau taux (ex: 2350)"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                data-testid="exchange-rate-input"
                style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, width: 180, fontSize: 14 }}
              />
              <button
                onClick={saveExchangeRate}
                disabled={!newRate}
                data-testid="exchange-rate-save"
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#0d9488', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: newRate ? 1 : 0.5 }}
              >
                Mettre à jour
              </button>
            </div>

            {loading ? (
              <p>Chargement...</p>
            ) : (
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Description</th>
                      <th>Durée</th>
                      <th>Prix</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((service) => (
                      <tr key={service.service_id}>
                        <td>{service.name}</td>
                        <td>{service.description}</td>
                        <td>{service.duration_minutes} min</td>
                        <td>{Math.round(service.price_cdf || service.price || 0).toLocaleString()} CDF</td>
                        <td>
                          <div className="table-actions">
                            <button 
                              onClick={() => { setEditingService(service); setShowServiceForm(true); }}
                              data-testid={`edit-service-${service.service_id}`}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button 
                              onClick={() => deleteService(service.service_id)}
                              className="btn-danger"
                              data-testid={`delete-service-${service.service_id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Service Form Modal */}
            {showServiceForm && (
              <ServiceFormModal
                service={editingService}
                onSave={saveService}
                onClose={() => { setShowServiceForm(false); setEditingService(null); }}
              />
            )}
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div data-testid="admin-subscriptions">
            <div className="admin-section-header">
              <h2><CreditCard size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />Abonnements & Services Tiers</h2>
              <button
                className="btn-primary"
                onClick={() => { setEditingSub(null); setShowSubForm(true); }}
                data-testid="add-subscription-btn"
              >
                <Plus size={20} /> Ajouter un abonnement
              </button>
            </div>
            <p style={{ fontSize: 13.5, color: '#64748b', margin: '0 0 1rem' }}>
              Suivi de tous les services externes payants liés à l'application : coûts, cycles de facturation et échéances de renouvellement.
            </p>

            <div className="stats-grid" style={{ marginBottom: '1.25rem' }}>
              <div className="stat-card">
                <h3>Abonnements actifs</h3>
                <p className="stat-number">{subscriptions.filter(s => s.status === 'actif').length}</p>
              </div>
              <div className="stat-card">
                <h3>Coût mensuel fixe estimé</h3>
                <p className="stat-number" style={{ fontSize: '1.5rem' }}>
                  {subscriptions.filter(s => s.status === 'actif' && s.billing_cycle === 'mensuel').reduce((sum, s) => sum + (s.cost || 0), 0).toLocaleString('fr-FR')} <span style={{ fontSize: 14 }}>USD/EUR</span>
                </p>
              </div>
              <div className="stat-card">
                <h3>Coût annuel fixe estimé</h3>
                <p className="stat-number" style={{ fontSize: '1.5rem' }}>
                  {subscriptions.filter(s => s.status === 'actif' && s.billing_cycle === 'annuel').reduce((sum, s) => sum + (s.cost || 0), 0).toLocaleString('fr-FR')} <span style={{ fontSize: 14 }}>USD</span>
                </p>
              </div>
              <div className="stat-card">
                <h3>Facturés à l'usage</h3>
                <p className="stat-number">{subscriptions.filter(s => s.status === 'actif' && s.billing_cycle === 'usage').length}</p>
              </div>
            </div>

            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Usage</th>
                    <th>Coût</th>
                    <th>Cycle</th>
                    <th>Renouvellement</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub.subscription_id}>
                      <td>
                        <strong>{sub.name}</strong>
                        {sub.url && (
                          <><br /><a href={sub.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#0d9488' }}>Portail →</a></>
                        )}
                      </td>
                      <td style={{ fontSize: 13, maxWidth: 260 }}>{sub.purpose}</td>
                      <td>{sub.cost > 0 ? `${sub.cost} ${sub.billing_cycle === 'usage' ? '%' : sub.currency}` : (sub.billing_cycle === 'usage' ? 'Variable' : '—')}</td>
                      <td style={{ textTransform: 'capitalize' }}>{sub.billing_cycle === 'usage' ? "À l'usage" : sub.billing_cycle}</td>
                      <td>{sub.renewal_date ? formatDate(sub.renewal_date) : '—'}</td>
                      <td>
                        <span style={{
                          padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                          background: sub.status === 'actif' ? '#dcfce7' : sub.status === 'à renouveler' ? '#fef9c3' : '#fee2e2',
                          color: sub.status === 'actif' ? '#166534' : sub.status === 'à renouveler' ? '#854d0e' : '#991b1b'
                        }}>
                          {sub.status}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => { setEditingSub(sub); setShowSubForm(true); }}
                            data-testid={`edit-subscription-${sub.subscription_id}`}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => deleteSubscription(sub.subscription_id)}
                            className="btn-danger"
                            data-testid={`delete-subscription-${sub.subscription_id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {subscriptions.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>Aucun abonnement enregistré</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {showSubForm && (
              <SubscriptionFormModal
                subscription={editingSub}
                onSave={saveSubscription}
                onClose={() => { setShowSubForm(false); setEditingSub(null); }}
              />
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div data-testid="admin-reviews">
            <div className="admin-section-header">
              <h2><Star size={22} style={{ verticalAlign: 'middle', marginRight: 8, color: '#f59e0b' }} />Avis Patients</h2>
              <select
                value={reviewFilter}
                onChange={(e) => setReviewFilter(e.target.value)}
                data-testid="review-filter"
                style={{ padding: '.5rem .75rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
              >
                <option value="all">Tous</option>
                <option value="submitted">Visibles</option>
                <option value="hidden">Masqués</option>
              </select>
            </div>

            <div className="stats-grid" style={{ marginBottom: '1.25rem' }}>
              <div className="stat-card">
                <h3>Total d'avis</h3>
                <p className="stat-number">{reviewStats.total}</p>
              </div>
              <div className="stat-card">
                <h3>Note moyenne</h3>
                <p className="stat-number" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {reviewStats.average || '-'}
                  <Star size={22} fill="#f59e0b" stroke="#f59e0b" />
                </p>
              </div>
              <div className="stat-card">
                <h3>Répartition</h3>
                <div style={{ fontSize: 13, textAlign: 'left' }}>
                  {[5, 4, 3, 2, 1].map(n => (
                    <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{n}★</span>
                      <span style={{ fontWeight: 600 }}>{reviewStats.distribution?.[String(n)] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Patient</th>
                    <th>Médecin</th>
                    <th>Note</th>
                    <th>Commentaire</th>
                    <th>Statut</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Aucun avis pour le moment.</td></tr>
                  )}
                  {reviews.map(r => (
                    <tr key={r.token} data-testid={`review-row-${r.token}`}>
                      <td>{formatDate(r.submitted_at, '-')}</td>
                      <td>{r.patient_name || '-'}</td>
                      <td>{r.doctor_name || '-'}{r.doctor_specialty ? ` (${r.doctor_specialty})` : ''}</td>
                      <td>
                        <div style={{ display: 'inline-flex', gap: 2 }}>
                          {[1, 2, 3, 4, 5].map(i => (
                            <Star key={i} size={16} fill={i <= (r.rating || 0) ? '#f59e0b' : 'none'} stroke="#f59e0b" />
                          ))}
                        </div>
                      </td>
                      <td style={{ maxWidth: 320, fontSize: 13, color: '#475569' }}>
                        {r.comment ? `« ${r.comment} »` : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td>
                        <span style={{
                          padding: '2px 10px', borderRadius: 12, fontSize: 12,
                          background: r.status === 'hidden' ? '#fee2e2' : '#dcfce7',
                          color: r.status === 'hidden' ? '#991b1b' : '#166534',
                        }}>
                          {r.status === 'hidden' ? 'Masqué' : 'Visible'}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => toggleReviewVisibility(r.token, r.status)}
                          className="btn-sm"
                          data-testid={`review-toggle-${r.token}`}
                          style={{
                            padding: '4px 10px', borderRadius: 6,
                            background: r.status === 'hidden' ? '#10b981' : '#f59e0b',
                            color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12,
                          }}
                        >
                          {r.status === 'hidden' ? <><Eye size={14} /> Réafficher</> : <><EyeOff size={14} /> Masquer</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Journal Médical Tab (CMS) */}
        {activeTab === 'journal' && (
          <div data-testid="admin-journal">
            <div className="admin-section-header">
              <h2><BookOpen size={22} style={{ verticalAlign: 'middle', marginRight: 8, color: '#1565C0' }} />Journal Médical</h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="btn-secondary"
                  onClick={fetchArticles}
                  disabled={refreshingArticles}
                  data-testid="refresh-articles-button"
                  title="Actualiser la liste"
                >
                  <RefreshCw size={17} className={refreshingArticles ? 'spin' : ''} />
                  {refreshingArticles ? 'Actualisation…' : 'Actualiser'}
                </button>
                <button
                  className="btn-primary"
                  onClick={() => { setArticleLoadError(''); setEditingArticle(null); setShowArticleForm(true); }}
                  data-testid="add-article-btn"
                >
                  <Plus size={18} /> Nouvel article
                </button>
              </div>
            </div>

            {articleLoadingId && (
              <div
                role="status"
                data-testid="article-content-loading"
                style={{ marginBottom: 12, padding: 12, borderRadius: 8, background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}
              >
                Chargement du contenu…
              </div>
            )}
            {articleLoadError && (
              <div
                role="alert"
                data-testid="article-content-load-error"
                style={{ marginBottom: 12, padding: 12, borderRadius: 8, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}
              >
                {articleLoadError} Aucune modification n'est possible tant que le contenu n'est pas chargé.
              </div>
            )}

            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Catégorie</th>
                    <th>Auteur</th>
                    <th>Statut</th>
                    <th>Créé le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                      Aucun article publié pour l'instant.
                    </td></tr>
                  )}
                  {articles.map(a => (
                    <tr key={a.article_id} data-testid={`article-row-${a.article_id}`}>
                      <td style={{ maxWidth: 360, fontWeight: 600 }}>{a.title}</td>
                      <td>{a.category || '-'}</td>
                      <td>{a.author || '-'}</td>
                      <td>
                        <span style={{
                          padding: '2px 10px', borderRadius: 12, fontSize: 12,
                          background: a.published ? '#dcfce7' : '#fee2e2',
                          color: a.published ? '#166534' : '#991b1b',
                        }}>
                          {a.published ? 'Publié' : 'Brouillon'}
                        </span>
                      </td>
                      <td>{formatDate(a.created_at, '-')}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => openArticleForEdit(a)}
                            disabled={!!articleLoadingId}
                            data-testid={`edit-article-${a.article_id}`}
                            title="Modifier"
                          >
                            {articleLoadingId === a.article_id ? <RefreshCw size={16} className="spin" /> : <Edit2 size={16} />}
                          </button>
                          <button
                            onClick={() => deleteArticle(a.article_id)}
                            className="btn-danger"
                            data-testid={`delete-article-${a.article_id}`}
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {showArticleForm && (
              <ArticleFormModal
                article={editingArticle}
                onSave={saveArticle}
                onClose={closeArticleForm}
                contentLoaded={!editingArticle || editingArticle.__contentLoaded === true}
                saving={savingArticle}
              />
            )}
          </div>
        )}

        {/* Messages de contact Tab */}
        {activeTab === 'messages' && (
          <div data-testid="admin-messages">
            <div className="admin-section-header">
              <h2><Mail size={22} style={{ verticalAlign: 'middle', marginRight: 8, color: '#1565C0' }} />Messages du site</h2>
              <select
                value={contactFilter}
                onChange={(e) => setContactFilter(e.target.value)}
                data-testid="messages-filter"
                style={{ padding: '.5rem .75rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
              >
                <option value="all">Tous</option>
                <option value="new">Nouveaux</option>
                <option value="handled">Traités</option>
              </select>
            </div>

            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Expéditeur</th>
                    <th>Contact</th>
                    <th>Objet</th>
                    <th>Message</th>
                    <th>Statut</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {contactMessages.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                      Aucun message reçu.
                    </td></tr>
                  )}
                  {contactMessages.map(m => (
                    <tr key={m.message_id} data-testid={`message-row-${m.message_id}`}>
                      <td>{formatDateTime(m.created_at, '-')}</td>
                      <td>{m.firstname} {m.name}</td>
                      <td style={{ fontSize: 13 }}>
                        <div>{m.phone}</div>
                        {m.email && <div style={{ color: '#64748b' }}>{m.email}</div>}
                      </td>
                      <td>{m.subject || '-'}</td>
                      <td style={{ maxWidth: 380, fontSize: 13, color: '#475569', whiteSpace: 'pre-wrap' }}>{m.message}</td>
                      <td>
                        <span style={{
                          padding: '2px 10px', borderRadius: 12, fontSize: 12,
                          background: m.status === 'handled' ? '#dcfce7' : '#fef3c7',
                          color: m.status === 'handled' ? '#166534' : '#92400e',
                        }}>
                          {m.status === 'handled' ? 'Traité' : 'Nouveau'}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => toggleContactStatus(m.message_id, m.status)}
                          className="btn-sm"
                          data-testid={`message-toggle-${m.message_id}`}
                          style={{
                            padding: '4px 10px', borderRadius: 6,
                            background: m.status === 'handled' ? '#f59e0b' : '#10b981',
                            color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12,
                          }}
                        >
                          {m.status === 'handled' ? 'Rouvrir' : 'Marquer traité'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Partners Tab */}
        {activeTab === 'partners' && (
          <AdminPartners token={localStorage.getItem('admin_token')} />
        )}

        {/* SerdiPay Reconciliation Tab */}
        {activeTab === 'payments-reconcile' && (
          <AdminPaymentsReconcile refreshKey={paymentsRefreshKey} />
        )}

      </div>
    </div>
  );
}


export default AdminDashboard;
